import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Twitter, Instagram, Linkedin, Lock } from 'lucide-react';

const Footer = () => {
  return (
    <footer className="bg-[#f5f0e8] pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Logo and tagline */}
        <div className="mb-12">
          <div className="mb-4">
            <img 
              src="/2026_logo.png" 
              alt="Student Signal" 
              className="h-[60px] w-auto"
              style={{ imageRendering: '-webkit-optimize-contrast' }}
            />
          </div>
          <p className="text-gray-600 text-sm max-w-md">
            Discover the colleges and scholarships that are right for you.
          </p>
        </div>

        {/* Footer links grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 mb-12">
          <div>
            <h3 className="font-semibold text-gray-900 mb-4">COLLEGE SEARCH</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/colleges" className="text-sm text-gray-600 hover:text-[#1a5d3a] transition-colors">
                  Search Colleges
                </Link>
              </li>
              <li>
                <Link to="/colleges" className="text-sm text-gray-600 hover:text-[#1a5d3a] transition-colors">
                  Compare Colleges
                </Link>
              </li>
              <li>
                <Link to="/colleges" className="text-sm text-gray-600 hover:text-[#1a5d3a] transition-colors">
                  College Reviews
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 mb-4">SCHOLARSHIPS</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/scholarships" className="text-sm text-gray-600 hover:text-[#1a5d3a] transition-colors">
                  Scholarships
                </Link>
              </li>
              <li>
                <Link to="/grants" className="text-sm text-gray-600 hover:text-[#1a5d3a] transition-colors">
                  Grants
                </Link>
              </li>
              <li>
                <Link to="/financial-aid" className="text-sm text-gray-600 hover:text-[#1a5d3a] transition-colors">
                  Financial Aid
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 mb-4">TOOLS & REVIEWS</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/career-finder" className="text-sm text-gray-600 hover:text-[#1a5d3a] transition-colors">
                  Career Finder
                </Link>
              </li>
              <li>
                <Link to="/coming-soon/school-match-quiz" className="text-sm text-gray-600 hover:text-[#1a5d3a] transition-colors">
                  School Match Quiz
                </Link>
              </li>
              <li>
                <Link to="/student-stories" className="text-sm text-gray-600 hover:text-[#1a5d3a] transition-colors">
                  Student Stories
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 mb-4">RESOURCES</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/application-tips" className="text-sm text-gray-600 hover:text-[#1a5d3a] transition-colors">
                  Application Tips
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 mb-4">ABOUT</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/about" className="text-sm text-gray-600 hover:text-[#1a5d3a] transition-colors">
                  Student Signal
                </Link>
              </li>
              <li>
                <Link to="/staff-login" className="text-sm text-gray-600 hover:text-[#1a5d3a] transition-colors inline-flex items-center gap-1">
                  Staff Only
                  <Lock size={14} />
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Social links and work link */}
        <div className="flex flex-col md:flex-row items-center justify-between border-t border-gray-300 pt-8">
          <div className="flex items-center gap-4 mb-4 md:mb-0">
            <span className="text-sm text-gray-600">Do you work for a school or college?</span>
            <Link to="/for-schools" className="text-sm text-[#1a5d3a] font-semibold hover:underline">
              Claim Your School
            </Link>
          </div>

          <div className="flex items-center gap-4">
            <a href="#" className="text-gray-500 hover:text-[#1a5d3a] transition-colors">
              <Facebook size={20} />
            </a>
            <a href="#" className="text-gray-500 hover:text-[#1a5d3a] transition-colors">
              <Twitter size={20} />
            </a>
            <a href="#" className="text-gray-500 hover:text-[#1a5d3a] transition-colors">
              <Instagram size={20} />
            </a>
            <a href="#" className="text-gray-500 hover:text-[#1a5d3a] transition-colors">
              <Linkedin size={20} />
            </a>
          </div>
        </div>

        {/* Bottom links */}
        <div className="flex flex-wrap items-center justify-center gap-4 mt-8 text-xs text-gray-500">
          <span>© 2026 Student Signal</span>
          <Link to="/privacy" className="hover:text-[#1a5d3a]">Privacy Policy</Link>
          <Link to="/terms" className="hover:text-[#1a5d3a]">Terms of Service</Link>
          <Link to="/accessibility" className="hover:text-[#1a5d3a]">Accessibility</Link>
        </div>

        {/* App store buttons */}
        <div className="flex items-center justify-center gap-4 mt-6">
          <a href="#" className="flex items-center gap-2 bg-black text-white px-4 py-2 rounded-lg text-sm hover:bg-gray-800 transition-colors">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
            </svg>
            <span>App Store</span>
          </a>
          <a href="#" className="flex items-center gap-2 bg-black text-white px-4 py-2 rounded-lg text-sm hover:bg-gray-800 transition-colors">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 0 1 0 1.73l-2.808 1.626L15.206 12l2.492-2.491zM5.864 2.658L16.8 8.99l-2.302 2.302-8.634-8.634z"/>
            </svg>
            <span>Google Play</span>
          </a>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
